/**
 * Mastodon platform adapter — created via the ActivityPub adapter factory.
 * Wraps mastodonService functions and passes them into createActivityPubAdapter.
 */
import {
    type MastodonSession,
    lookupAccount,
    getRelationship,
    followAccount,
    unfollowAccount,
} from '@/lib/mastodon'
import {
    fetchMastodonTimeline,
    fetchMastodonListTimeline,
    fetchMastodonProfilePosts,
    favouriteMastodonStatus,
    unfavouriteMastodonStatus,
    reblogMastodonStatus,
    unreblogMastodonStatus,
    deleteMastodonStatus,
    fetchMastodonFavourites,
    bookmarkMastodonStatus,
    unbookmarkMastodonStatus,
    pinMastodonStatus,
    unpinMastodonStatus,
    fetchMastodonBookmarks,
    resolveLocalStatusId,
    blockMastodonUser,
    unblockMastodonUser,
    fetchMastodonBlocks,
} from '@/services/mastodonService'
import { fetchMastodonHashtagPosts } from '@/services/hashtagService'
import { searchMastodonUsers, searchMastodonPosts } from '@/services/searchService'
import {
    fetchMastodonNotifications,
    fetchMastodonUnreadCount,
} from '@/services/notificationsService'
import { createActivityPubAdapter } from './activityPubAdapter'

export const mastodonAdapter = createActivityPubAdapter({
    platform: 'mastodon',
    timelinePrefix: 'mastodon',
    supportsListTimeline: true,
    supportsHashtagTimeline: true,

    capabilities: {
        reply: true,
        like: true,
        repost: true,
        quote: false,
        delete: true,
        follow: true,
        feeds: false,
        hashtags: true,
        gridView: false,
        likedPostsFeed: false,
        favouritesFeed: true,
        profileRepliesTab: true,
        bookmarks: true,
        pinPosts: true,
        lists: true,
    },

    services: {
        fetchTimeline: (session, options) =>
            fetchMastodonTimeline(session as MastodonSession, options),

        fetchListTimeline: (session, listId, options) =>
            fetchMastodonListTimeline(session as MastodonSession, listId, options),

        favouriteStatus: (session, id) => favouriteMastodonStatus(session as MastodonSession, id),

        unfavouriteStatus: (session, id) =>
            unfavouriteMastodonStatus(session as MastodonSession, id),

        reblogStatus: (session, id) => reblogMastodonStatus(session as MastodonSession, id),

        unreblogStatus: (session, id) => unreblogMastodonStatus(session as MastodonSession, id),

        deleteStatus: (session, id) => deleteMastodonStatus(session as MastodonSession, id),

        fetchFavourites: (session, options) =>
            fetchMastodonFavourites(session as MastodonSession, options),

        bookmarkStatus: (session, id) => bookmarkMastodonStatus(session as MastodonSession, id),

        unbookmarkStatus: (session, id) => unbookmarkMastodonStatus(session as MastodonSession, id),

        pinStatus: (session, id) => pinMastodonStatus(session as MastodonSession, id),

        unpinStatus: (session, id) => unpinMastodonStatus(session as MastodonSession, id),

        fetchBookmarks: (session, options) =>
            fetchMastodonBookmarks(session as MastodonSession, options),

        searchUsers: (query, session) => searchMastodonUsers(query, session as MastodonSession),

        searchPosts: (query, session, offset) =>
            searchMastodonPosts(query, session as MastodonSession, offset),

        fetchNotifications: (session, options, platform) =>
            fetchMastodonNotifications(session as MastodonSession, options, platform),

        fetchUnreadCount: session => fetchMastodonUnreadCount(session as MastodonSession),

        fetchProfilePosts: (identifier, options) =>
            fetchMastodonProfilePosts(identifier, {
                cursor: options.cursor,
                mastodonSession: options.session as MastodonSession,
            }),

        fetchHashtagPosts: (tag, session, cursor) =>
            fetchMastodonHashtagPosts(tag, session as MastodonSession, cursor),

        lookupAccount: (session, identifier) =>
            lookupAccount(session as MastodonSession, identifier),

        getRelationship: (session, id) => getRelationship(session as MastodonSession, id),

        followAccount: (session, id) => followAccount(session as MastodonSession, id),

        unfollowAccount: (session, id) => unfollowAccount(session as MastodonSession, id),

        blockUser: (session, id) => blockMastodonUser(session as MastodonSession, id),

        unblockUser: (session, id) => unblockMastodonUser(session as MastodonSession, id),

        fetchBlocks: (session, options) => fetchMastodonBlocks(session as MastodonSession, options),

        resolveLocalStatusId: (session, uri, statusId) =>
            resolveLocalStatusId(session as MastodonSession, uri, statusId),
    },
})
